import { FC, MouseEventHandler, ReactNode } from 'react';
import Grid, { GridProps } from '../Grid';
import DialogTitle from '../DialogTitle';
import Typography from '../Typography';
import IconButton from '../IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { TypographyVariant } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DIALOG_SIDE_PADDINGS } from '../theme/constants';
import palette from '../theme/palette';
import { useDialogHeaderUtilityClasses } from './dialogHeaderClasess';
import { clsx } from 'clsx';

type MuiGridProps = Omit<GridProps, 'title'>;

export interface DialogHeaderElementIds {
  title?: string;
  subtitle?: string;
  closeBtn?: string;
}

export interface DialogHeaderProps extends MuiGridProps {
  title?: ReactNode;
  subTitle?: ReactNode;
  onClose?: MouseEventHandler;
  titleVariant?: TypographyVariant;
  /**
   * @deprecated Use `subTitleVariant` (capital T) instead.
   */
  subtitleVariant?: TypographyVariant;
  subTitleVariant?: TypographyVariant;
  divider?: boolean;
  elmIds?: DialogHeaderElementIds;
}

const HeaderGrid = styled(Grid, {
  name: 'DialogHeader',
  shouldForwardProp: (prop) => prop !== 'divider',
})<{ divider: DialogHeaderProps['divider'] }>(({ theme, divider }) => ({
  padding: `12px ${DIALOG_SIDE_PADDINGS.XS} 0`,
  [theme.breakpoints.up('sm')]: {
    padding: `24px ${DIALOG_SIDE_PADDINGS.SM} 0`,
  },
  ...(divider && {
    borderBottom: `1px solid ${palette.divider}`,
    padding: `12px ${DIALOG_SIDE_PADDINGS.XS} 12px`,
    [theme.breakpoints.up('sm')]: {
      padding: `16px ${DIALOG_SIDE_PADDINGS.SM} 12px`,
    },
  }),
  '& + .MuiDialogActions-root': {
    paddingTop: '24px',
  },
}));

const DialogHeader: FC<DialogHeaderProps> = ({
  title,
  subTitle,
  onClose,
  titleVariant = 'h5',
  subtitleVariant = 'body1',
  subTitleVariant = subtitleVariant,
  elmIds,
  divider = false,
  ...props
}) => {
  const utilityClasses = useDialogHeaderUtilityClasses();

  return (
    <HeaderGrid
      container
      className={clsx(utilityClasses.root)}
      wrap="nowrap"
      divider={divider}
      gap={1}
      justifyContent="space-between"
      {...props}
    >
      <Grid item className={clsx(utilityClasses.titlesWrapper)} flexGrow={1}>
        <DialogTitle data-elm={elmIds?.title} className={clsx(utilityClasses.title)} variant={titleVariant}>
          {title}
        </DialogTitle>
        <Typography
          data-elm={elmIds?.subtitle}
          variant={subTitleVariant}
          className={clsx(utilityClasses.subTitle)}
          sx={{ color: (theme) => theme.palette.text.primary }}
        >
          {subTitle}
        </Typography>
      </Grid>
      <Grid item className={clsx(utilityClasses.closeBtnWrapper)}>
        {onClose && (
          <IconButton
            data-elm={elmIds?.closeBtn}
            onClick={onClose}
            className={clsx(utilityClasses.closeBtn)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon className={clsx(utilityClasses.closeBtn)} />
          </IconButton>
        )}
      </Grid>
    </HeaderGrid>
  );
};

export default DialogHeader;
